import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { SimpleDropdownOption } from '@components/simple-dropdown/simple-dropdown.component';
import { GraphsService } from '@service/graphs/graphs.service';
import { Graph, GraphType, GraphTypeTranslate } from 'icreate/common/models/graphs';
import { Vessel } from 'icreate/common/models/vessel';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { GraphValidator } from 'icreate/common/validators/graph/graph-validator';
import _ from 'lodash';
import { lastValueFrom, take } from 'rxjs';

@Component({
    selector: 'app-new-graph-dialog',
    templateUrl: './new-graph-dialog.component.html',
    styleUrl: './new-graph-dialog.component.css',
    standalone: false,
})
export class NewGraphDialogComponent {
    readonly TYPE_OPTIONS: SimpleDropdownOption[] = [
        { value: GraphType.Exhaust, string: 'graph.type.exhaust' },
        { value: GraphType.Bar, string: 'graph.type.bar' },
        { value: GraphType.Gauge, string: 'graph.type.gauge' },
    ];

    @ViewChild('cancelConfirmation') cancelConfirmation: ConfirmationWindowComponent;
    title: string;
    selectedType: GraphType;
    graphList: Graph[];
    vessel: Vessel;

    isEditing: boolean = false;
    isSubmiting: boolean = false;
    initialType;
    graphTypes = GraphTypeTranslate;
    initialTitle: string;
    validationErrors: ValidationErrorMap = {};
    hasError: boolean;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            graphList: Graph[];
            vessel: Vessel;
            graph: Graph;
        },
        private dialogRef: MatDialogRef<NewGraphDialogComponent>,
        private graphService: GraphsService
    ) {}

    ngOnInit(): void {
        if (this.data.graphList) {
            this.graphList = this.data.graphList;
        }
        if (this.data.vessel) {
            this.vessel = this.data.vessel;
        }

        if (this.data.graph) {
            this.isEditing = true;
            this.title = this.data.graph.title;
            this.selectedType = this.data.graph.type;
        }

        this.initialTitle = this.title;
        this.dialogHasErrors();
    }

    async createGraph() {
        if (!this.isEditing && this.isCreateDisabled) return;
        else if (this.isEditing && this.isEditDisabled) return;
        this.isSubmiting = true;
        if (this.isEditing) {
            this.dialogRef.close(this.title);
            this.isSubmiting = false;
        } else if (this.title) {
            const newGraph = new Graph();
            newGraph.title = this.title;
            newGraph.type = this.selectedType;
            newGraph.vesselId = this.vessel.id;

            try {
                this.graphService.showLoadingSpinner(true);
                const savedGraph: any = await this.graphService.createGraph(newGraph);
                if (savedGraph && savedGraph.id) newGraph.id = savedGraph.id;
                this.dialogRef.close(newGraph);
            } catch (err) {
                console.error(err);
                this.isSubmiting = false;
            } finally {
                this.graphService.showLoadingSpinner(false);
            }
        }
    }

    get isCreateDisabled() {
        return this.hasError || (!this.title && this.title != '' && !this.selectedType);
    }

    get isEditDisabled() {
        return this.hasError || this.title == this.data.graph.title;
    }

    dialogHasErrors() {
        const newGraph = {
            title: this.title,
            type: this.selectedType,
            vesselId: this.vessel.id,
        };

        const errors = getValidationErrors(GraphValidator(), newGraph);
        const mappedErrors: { [key: string]: { message: string } } = mapErrors(errors) || {};

        let isRepeated = false;
        if (this.graphList && this.graphList.length > 0) {
            this.graphList.forEach(graph => {
                if (this.data.graph?.id !== graph.id && graph.title === this.title && !this.isSubmiting) {
                    isRepeated = true;
                }
            });
        }

        if (isRepeated) {
            mappedErrors['title'] = { message: 'graph.create.dialog.title.error.repeated' };
        }

        this.validationErrors = Object.keys(mappedErrors).length > 0 ? mappedErrors : null;

        const propertiesToCheck = Object.keys(newGraph);
        for (const property of propertiesToCheck) {
            if (property in mappedErrors) {
                this.hasError = true;
                return;
            }
        }

        this.hasError = false;
    }

    async cancel() {
        if (!this.isCreateDisabled && !this.isEditing) {
            this.cancelConfirmation.setVisible(true);
            const confirmationOption = await lastValueFrom(this.cancelConfirmation.onOptionSelected.pipe(take(1)));
            if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                this.dialogRef.close();
                return;
            } else if (confirmationOption === ConfirmationWindowTypes.DISCARD) {
                return;
            }
        }
        this.dialogRef.close();
    }

    onInputChange(event: { name: string; value: string }) {
        this.title = event.value;
        this.dialogHasErrors();
    }

    handleTypeChanged(event) {
        this.selectedType = event.value;
        this.dialogHasErrors();
    }
}
